import type { SequenceArmPathSeg } from '@poseidon/types/types/sequenceOverrideKey';

import type { SequenceStep } from '~/components/Sequence/sequenceSteps';
import { canMergeIntoGroup, canReceiveMerge } from '~/components/Sequence/sequenceSteps';

/**
 * Cross-surface drag/move engine for the sequence graph. The editor's drag layer resolves a gesture into a
 * `from` step + a `drop`, then calls {@link moveStep} — a single pure function that reorders a step within
 * ANY line OR moves it between lines OR merges it onto a node to form / join a parallel group.
 *
 * A "line" is any ordered list of steps you can drag within: the spine, a spawn's arm, a switch branch's
 * arm, a parallel group's chips. Lines NEST — a switch branch can hold a spawn whose arm holds a group —
 * so a line is addressed by a PATH of container hops from the spine ({@link LineAddr}), not by a fixed
 * one-level shape. That is the whole point: every line at every depth has a name, so every step is both a
 * drag source and a drop target, and one tested function covers every gesture.
 */

/** One hop from a line into a container step's own line: which step (`at`), and which of its lines. */
export type LineHop =
  /** `steps[at].arm` — a spawn's detached branch. */
  | { at: number; into: 'spawn' }
  /** `steps[at].switch.branches[index].arm` — one route branch of a switch. */
  | { at: number; into: 'branch'; index: number }
  /** `steps[at].arms` flattened to one step per arm — a parallel group's chip row. */
  | { at: number; into: 'group' };

/** A line of steps: the spine (`path: []`) or a nested container line reached by walking `path`. */
export interface LineAddr {
  path: LineHop[];
}

/** The main line. */
export const SPINE: LineAddr = { path: [] };

/** Extend `line` with one more hop — the line owned by the container step at `line[index]`. */
export function childLine(line: LineAddr, hop: LineHop): LineAddr {
  return { path: [...line.path, hop] };
}

/** Serialize a line address into a `data-line` DOM attribute the drag hit-test can read back. */
export function serializeLine(line: LineAddr): string {
  if (line.path.length === 0) return 'spine';
  return line.path.map((h) => (h.into === 'spawn' ? `${h.at}s` : h.into === 'group' ? `${h.at}g` : `${h.at}b${h.index}`)).join('.');
}

const HOP_RE = /^(\d+)(?:(s)|(g)|b(\d+))$/;

/** Parse a `data-line` attribute back into a {@link LineAddr}, or null if malformed. */
export function parseLine(raw: string | null): LineAddr | null {
  if (raw == null) return null;
  if (raw === 'spine') return SPINE;
  const path: LineHop[] = [];
  for (const part of raw.split('.')) {
    const m = HOP_RE.exec(part);
    if (m == null) return null;
    const at = Number(m[1]);
    if (m[2] != null) path.push({ at, into: 'spawn' });
    else if (m[3] != null) path.push({ at, into: 'group' });
    else path.push({ at, into: 'branch', index: Number(m[4]) });
  }
  return { path };
}

/** A specific step: the line it lives in + its index there. */
export interface StepAddr {
  line: LineAddr;
  index: number;
}

/** Where a dragged step lands: reordered into a line at a gap (0..len), or merged onto another node. */
export type DropTarget =
  | { kind: 'reorder'; line: LineAddr; gap: number }
  | { kind: 'merge'; onto: StepAddr };

/** The line a drop lands in, whichever form it takes. */
export function dropLine(drop: DropTarget): LineAddr {
  return drop.kind === 'merge' ? drop.onto.line : drop.line;
}

/** Two line addresses point at the same list. */
export function sameLine(a: LineAddr, b: LineAddr): boolean {
  return serializeLine(a) === serializeLine(b);
}

/** `inner`'s path starts with the whole of `outer`'s (they may be equal). */
function startsWith(inner: LineAddr, outer: LineAddr): boolean {
  if (inner.path.length < outer.path.length) return false;
  return serializeLine({ path: inner.path.slice(0, outer.path.length) }) === serializeLine(outer);
}

/**
 * `line` lives INSIDE the step at `addr` — the container's own line, or anything nested below it. Dropping
 * a container into its own subtree would splice the step into a list it owns (it would vanish, taking its
 * contents with it), so {@link canDrop} rejects it.
 */
export function lineIsInsideStep(line: LineAddr, addr: StepAddr): boolean {
  if (!startsWith(line, addr.line)) return false;
  const next = line.path[addr.line.path.length];
  return next != null && next.at === addr.index;
}

/** A group's chip row: one step per arm (the shape the group editor renders and writes back). */
function flattenGroupArms(step: SequenceStep): SequenceStep[] {
  return (step.arms ?? []).map((a) => a[0]).filter((s): s is SequenceStep => s != null);
}

/** Read the ordered steps of a line (empty array if the address doesn't resolve). */
export function getLine(steps: SequenceStep[], line: LineAddr): SequenceStep[] {
  let cur = steps;
  for (const hop of line.path) {
    const parent = cur[hop.at];
    if (parent == null) return [];
    if (hop.into === 'spawn') cur = parent.arm ?? [];
    else if (hop.into === 'branch') cur = parent.switch?.branches[hop.index]?.arm ?? [];
    else cur = flattenGroupArms(parent);
  }
  return cur;
}

/**
 * Whether `line` actually RESOLVES — every hop lands on a container of the matching type. An address is
 * read from the DOM at pointer-down and applied on release, so it can go stale (a step moved, a container
 * was removed). Without this check a move would remove the step from its source line and then find nothing
 * to insert into, destroying it.
 */
export function lineExists(steps: SequenceStep[], line: LineAddr): boolean {
  let cur = steps;
  for (const hop of line.path) {
    const parent = cur[hop.at];
    if (parent == null) return false;
    if (hop.into === 'spawn') {
      if (parent.type !== 'spawn') return false;
      cur = parent.arm ?? [];
    } else if (hop.into === 'branch') {
      const branch = parent.type === 'switch' ? parent.switch?.branches[hop.index] : undefined;
      if (branch == null) return false;
      cur = branch.arm;
    } else {
      if (parent.type !== 'group') return false;
      cur = flattenGroupArms(parent);
    }
  }
  return true;
}

/** The step at an address, or null. */
export function stepAt(steps: SequenceStep[], addr: StepAddr): SequenceStep | null {
  return getLine(steps, addr.line)[addr.index] ?? null;
}

/** Immutably replace a line's step array via `fn`, returning the new spine. */
export function updateLine(steps: SequenceStep[], line: LineAddr, fn: (arr: SequenceStep[]) => SequenceStep[]): SequenceStep[] {
  const [hop, ...rest] = line.path;
  if (hop == null) return fn(steps);
  const restLine: LineAddr = { path: rest };
  return steps.map((s, i) => {
    if (i !== hop.at) return s;
    // Every hop is guarded on the container's TYPE: an address can go stale (read at pointer-down, applied
    // after a re-render moved the step), and an unguarded hop would graft an `arm` / `arms` payload onto
    // whatever plain step now sits at that index.
    if (hop.into === 'spawn') return s.type === 'spawn' ? { ...s, arm: updateLine(s.arm ?? [], restLine, fn) } : s;
    if (hop.into === 'branch') {
      if (s.type !== 'switch' || s.switch == null) return s;
      const branches = s.switch.branches.map((b, bi) => (bi === hop.index ? { ...b, arm: updateLine(b.arm, restLine, fn) } : b));
      return { ...s, switch: { ...s.switch, branches } };
    }
    if (s.type !== 'group') return s;
    // A group's chip row is a PROJECTION of its arms (each chip is one arm's first step). Writing the row
    // back must not flatten the arms away: an arm may legitimately hold more than one step (normalizeStep
    // preserves them and the engine walks them), so re-attach each arm's tail by tracking the step OBJECT
    // through the move — identity travels with it, so a tail follows its head across a reorder.
    const armFor = new Map<SequenceStep, SequenceStep[]>();
    for (const arm of s.arms ?? []) if (arm[0] != null) armFor.set(arm[0], arm.slice(1));
    const nextRow = updateLine(flattenGroupArms(s), restLine, fn);
    return { ...s, arms: nextRow.map((c) => [c, ...(armFor.get(c) ?? [])]), join: 'all' as const };
  });
}

/** Turn the node at `idx` into (or grow) a parallel group by adding `step` as a new arm (target first). */
function mergeInto(arr: SequenceStep[], idx: number, step: SequenceStep): SequenceStep[] {
  const target = arr[idx];
  if (target == null) return arr;
  const next = arr.slice();
  if (target.type === 'group') next[idx] = { ...target, arms: [...(target.arms ?? []), [step]], join: 'all' };
  else next[idx] = { type: 'group', join: 'all', arms: [[target], [step]] };
  return next;
}

/**
 * Whether a `step` may LIVE in `line` — the same container rule the "+" picker enforces and the server's
 * `normalizeStep` applies (pg.sequence.service `ContainerMode`):
 *
 * | line                          | mode     | allows                          |
 * |-------------------------------|----------|---------------------------------|
 * | spine                         | `all`    | anything, incl. `switch`        |
 * | spawn arm / switch branch arm | `branch` | anything but `switch`           |
 * | parallel group chip row       | `leaf`   | single steps only (no container)|
 *
 * A `switch`'s fan only renders at the top, so it stays top-only; a group's arms are leaves. Drag can
 * therefore never build a tree the picker (or the server) would reject.
 */
export function canMoveToLine(step: SequenceStep, line: LineAddr): boolean {
  const last = line.path[line.path.length - 1];
  if (last == null) return true; // spine — `all`
  const isContainer = step.type === 'group' || step.type === 'spawn' || step.type === 'switch';
  if (last.into === 'group') return !isContainer; // `leaf`
  return step.type !== 'switch'; // `branch`
}

/**
 * Whether `from` can be dropped as `drop`. A step must be allowed to live in the target line
 * ({@link canMoveToLine}), may never land inside itself ({@link lineIsInsideStep}), must be groupable to
 * merge, and a reorder that doesn't move it is a no-op. Mirrors the spine's guards on every surface, so
 * the same gesture is accepted (or refused) identically wherever you make it.
 */
export function canDrop(steps: SequenceStep[], from: StepAddr, drop: DropTarget): boolean {
  const step = stepAt(steps, from);
  if (step == null) return false;
  // A group's chip row is a lossy PROJECTION of its arms — one chip per arm, showing only the arm's first
  // step. An arm holding more than one step therefore cannot be relocated through it: the move would carry
  // the head and strand the tail. The UI never authors such an arm (every group edit writes single-step
  // arms), but a converted or API-authored sequence can, so refuse rather than silently drop the tail.
  const fromHop = from.line.path[from.line.path.length - 1];
  if (fromHop?.into === 'group') {
    const parent = stepAt(steps, { line: { path: from.line.path.slice(0, -1) }, index: fromHop.at });
    if ((parent?.arms?.[from.index]?.length ?? 1) > 1) return false;
  }
  // The target line has to be real — a stale address would remove the step and land it nowhere.
  if (!lineExists(steps, dropLine(drop))) return false;
  // A container can't be dropped into its own subtree (it would swallow itself).
  if (lineIsInsideStep(dropLine(drop), from)) return false;

  if (drop.kind === 'merge') {
    if (sameLine(drop.onto.line, from.line) && drop.onto.index === from.index) return false; // onto itself
    // ...nor onto the container it already lives in. Dropping a group's only chip back on the group box
    // would empty the group (so `collapseEmptied` removes it) while the merge still points at that index —
    // the step lands nowhere and is silently destroyed, or worse, folds an unrelated neighbour into a group.
    if (lineIsInsideStep(from.line, drop.onto)) return false;
    // A merge wraps the two steps into a group AT the target line — so the group must be allowed there, and
    // the dragged step must be groupable and the target able to receive it.
    return canMoveToLine({ type: 'group' }, drop.onto.line) && canMergeIntoGroup(step) && canReceiveMerge(stepAt(steps, drop.onto));
  }
  if (!canMoveToLine(step, drop.line)) return false;
  // A switch ENDS the main line — it must stay the last spine step, and nothing may land after it (that
  // step could never run). So a spine reorder can't drop a step past the switch, nor move the switch itself
  // off the end.
  if (drop.line.path.length === 0) {
    const switchIdx = steps.findIndex((s) => s.type === 'switch');
    if (switchIdx >= 0) {
      const fromSpine = from.line.path.length === 0;
      const removedBefore = fromSpine && from.index < switchIdx ? 1 : 0;
      const switchAfter = switchIdx - removedBefore;
      const insertAt = fromSpine && drop.gap > from.index ? drop.gap - 1 : drop.gap;
      if (step.type === 'switch') {
        if (insertAt < steps.length - 1) return false; // the switch itself must stay last
      } else if (insertAt > switchAfter) {
        return false; // a normal step can't land after the switch
      }
    }
  }
  // A reorder to the gap just before or after the step's own slot (same line) leaves the order unchanged.
  if (sameLine(drop.line, from.line) && (drop.gap === from.index || drop.gap === from.index + 1)) return false;
  return true;
}

/**
 * A line is addressed THROUGH the steps that contain it, so removing a step shifts every address that
 * hops past it. Re-point `line` after the step at `removed` is spliced out of its list: if `line` descends
 * through that same list at a later index, that hop shifts left by one.
 */
function shiftLine(line: LineAddr, removed: StepAddr): LineAddr {
  const depth = removed.line.path.length;
  if (!startsWith(line, removed.line) || line.path.length <= depth) return line;
  const hop = line.path[depth];
  if (hop == null || hop.at <= removed.index) return line;
  const path = line.path.slice();
  path[depth] = { ...hop, at: hop.at - 1 };
  return { path };
}

/** Re-point a whole drop (its line AND its index within that line) after `removed` is spliced out. */
function shiftDrop(drop: DropTarget, removed: StepAddr): DropTarget {
  if (drop.kind === 'merge') {
    const line = shiftLine(drop.onto.line, removed);
    const index = sameLine(drop.onto.line, removed.line) && drop.onto.index > removed.index ? drop.onto.index - 1 : drop.onto.index;
    return { kind: 'merge', onto: { line, index } };
  }
  const line = shiftLine(drop.line, removed);
  const gap = sameLine(drop.line, removed.line) && drop.gap > removed.index ? drop.gap - 1 : drop.gap;
  return { kind: 'reorder', line, gap };
}

/**
 * A container is only meaningful with steps in it: emptying a `group` / `spawn` removes the node (the
 * server drops it on save anyway — `normalizeStep` returns null — so leaving one behind would show a node
 * that silently disappears on reload), and a group down to ONE arm collapses back to that lone step (a
 * group of one is just a step). An emptied switch BRANCH is legal and stays — a range alone is a complete
 * branch. Walks the drag source's container chain deepest-first, since collapsing one can empty its parent.
 *
 * Returns the pruned steps plus every removal made, so the caller can re-point the pending drop through
 * exactly the same shifts ({@link shiftDrop}).
 */
function collapseEmptied(steps: SequenceStep[], path: LineHop[]): { steps: SequenceStep[]; removed: StepAddr[] } {
  const removed: StepAddr[] = [];
  let cur = steps;
  for (let d = path.length; d >= 1; d--) {
    const hop = path[d - 1];
    if (hop == null) break;
    const parentLine: LineAddr = { path: path.slice(0, d - 1) };
    const line: LineAddr = { path: path.slice(0, d) };
    const contents = getLine(cur, line);
    if (contents.length === 0) {
      if (hop.into === 'branch') break; // an empty route branch is a complete branch — keep it
      const addr: StepAddr = { line: parentLine, index: hop.at };
      cur = updateLine(cur, parentLine, (arr) => arr.filter((_, i) => i !== hop.at));
      removed.push(addr);
      continue; // the parent line just lost a step too — it may now be empty itself
    }
    const lone = contents[0];
    if (hop.into === 'group' && contents.length === 1 && lone != null) {
      cur = updateLine(cur, parentLine, (arr) => arr.map((s, i) => (i === hop.at ? lone : s)));
    }
    break; // this container still holds something — its ancestors are unaffected
  }
  return { steps: cur, removed };
}

/**
 * Apply a drag: move `from` to `drop`. Reorders within a line, moves between lines at ANY depth (spine ↔
 * spawn arm ↔ switch branch ↔ group chips, nested arbitrarily), or merges onto a node to form/join a
 * parallel group. Returns the original array unchanged if the move isn't allowed ({@link canDrop}). Pure.
 */
export function moveStep(steps: SequenceStep[], from: StepAddr, drop: DropTarget): SequenceStep[] {
  if (!canDrop(steps, from, drop)) return steps;
  const step = stepAt(steps, from);
  if (step == null) return steps;

  // Remove the step from its source line first; every downstream index is computed on this array, with the
  // pending drop re-pointed through the same removal.
  let cur = updateLine(steps, from.line, (arr) => arr.filter((_, i) => i !== from.index));
  let target = shiftDrop(drop, from);

  // Leaving a line can empty its container — prune before inserting, re-pointing the drop each time.
  if (!sameLine(dropLine(drop), from.line)) {
    const pruned = collapseEmptied(cur, from.line.path);
    cur = pruned.steps;
    for (const r of pruned.removed) target = shiftDrop(target, r);
  }

  if (target.kind === 'merge') return updateLine(cur, target.onto.line, (arr) => mergeInto(arr, target.onto.index, step));
  return updateLine(cur, target.line, (arr) => {
    const next = arr.slice();
    next.splice(Math.max(0, Math.min(target.kind === 'reorder' ? target.gap : 0, next.length)), 0, step);
    return next;
  });
}

/**
 * The engine's `(path, cursor)` coordinates for a line step — the address `resolveArmSteps` walks and
 * {@link sequenceOverrideKey} formats, so a per-contact content override saved in the UI lands on the key
 * the send actually reads. Each hop contributes `stepIndex` plus its container tag (`'spawn'` / branch
 * index); a GROUP's chip index is itself the arm index, so it joins the path and the cursor is 0.
 */
export function lineStepCoords(line: LineAddr, index: number): { path: SequenceArmPathSeg[]; cursor: number } {
  const path: SequenceArmPathSeg[] = [];
  for (const hop of line.path) {
    path.push(hop.at);
    if (hop.into === 'spawn') path.push('spawn');
    else if (hop.into === 'branch') path.push(hop.index);
  }
  const last = line.path[line.path.length - 1];
  if (last?.into === 'group') return { path: [...path, index], cursor: 0 };
  return { path, cursor: index };
}
